// Tiny hash-based router. ASIS desktop sections are explicit and stable; the
// router exists so the active view survives page reload and the
// sidebar can deep-link.

export type Section = 'conversar' | 'espacio' | 'hoy' | 'escaneo' | 'personas' | 'grupos' | 'conocimiento' | 'conectores' | 'modelos' | 'dispositivos' | 'redes' | 'memoria' | 'actividad' | 'perfil' | 'ajustes' | 'login' | 'setup';

const VALID: ReadonlySet<Section> = new Set<Section>([
  'conversar', 'espacio', 'hoy', 'escaneo', 'personas', 'grupos', 'conocimiento', 'conectores', 'modelos', 'dispositivos', 'redes', 'memoria', 'actividad', 'perfil', 'ajustes', 'login', 'setup',
]);

const DEFAULT_BY_AUTH: Record<'auth' | 'anon', Section> = {
  auth: 'hoy',
  anon: 'login',
};

export type RouteListener = (current: Section, prev: Section) => void;

export class Router {
  private current: Section;
  private listeners = new Set<RouteListener>();
  private onChange: ((next: Section) => void) | null = null;

  constructor(initial: Section = 'hoy') {
    this.current = initial;
  }

  start(initialAuth: 'auth' | 'anon' = 'auth'): Section {
    this.current = this.readHash() ?? DEFAULT_BY_AUTH[initialAuth];
    window.addEventListener('hashchange', this.handleHash);
    this.onChange?.(this.current);
    return this.current;
  }

  stop(): void {
    window.removeEventListener('hashchange', this.handleHash);
  }

  setOnChange(fn: (next: Section) => void): void { this.onChange = fn; }

  subscribe(fn: RouteListener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  currentSection(): Section { return this.current; }

  go(next: Section): void {
    if (!VALID.has(next)) return;
    if (next === this.current) return;
    const target = `#/${next}`;
    if (window.location.hash !== target) {
      window.location.hash = target;
    }
    this.setCurrent(next);
  }

  private handleHash = (): void => {
    const next = this.readHash() ?? this.current;
    if (next !== this.current && VALID.has(next)) this.setCurrent(next);
  };

  private setCurrent(next: Section): void {
    const prev = this.current;
    this.current = next;
    this.onChange?.(next);
    for (const fn of this.listeners) {
      try { fn(next, prev); } catch { /* listener bug; never crash the app */ }
    }
  }

  private readHash(): Section | null {
    const h = window.location.hash.replace(/^#\/?/, '');
    if (!h) return null;
    return VALID.has(h as Section) ? (h as Section) : null;
  }
}
